import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import PropTypes from 'prop-types'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Dialog from '@mui/material/Dialog'
import Paper from '@mui/material/Paper'
import AnimatedPlaceholderTextField from './AnimatedPlaceholderTextField'
import { useAction } from 'utils/actions'
import { distributeTravelers, checkString } from 'utils/helpers'
import { 
  GET_TRIP,
  SEND_AI_CHAT, 
  GET_SEARCH_HISTORY,
  ADD_TO_SEARCH_HISTORY,
  ENCRYPT_TOKEN,
  GET_PAST_TRIPS,
  GET_GPT_EVERYTHING_SEARCH,
} from 'api/constants'
import {
  // setFlightOptions,
  setHotelOptions,
  // setExperienceOptions,
  setRentalCarOptions,
  setOceanCruiseOptions,
  setRiverCruiseOptions,
} from 'slices/travelSlice'
import { 
  // useFlightSearch, 
  useHotelSearch,
  useExperienceSearch,
  useCarSearch,
  useOceanCruiseSearch,
  useRiverCruiseSearch,
  getSearchHistoryElement,
  getName,
} from 'utils/tripUtils'
import { useGet, useBreakpoints } from 'utils/hooks'
import { isEmpty } from 'lodash'
import md5 from 'md5'
import country_capitals from 'data/country_capitals.json'
import { ReactComponent as IconBackArrow } from 'icons/BackArrow.svg'
import { ReactComponent as IconSearch } from 'icons/Search.svg'

const CONFIG = {
  // 'Flight': {
  //   reducer: setFlightOptions,
  //   to: '/shop-travel/flights',
  // },
  'Stay': {
    reducer: setHotelOptions,
    to: '/shop-travel/stays/hotels',
  },
  // 'Experience': {
  //   reducer: setExperienceOptions,
  //   to: '/shop-travel/things-to-do',
  // },
  'Car': {
    reducer: setRentalCarOptions,
    to: '/shop-travel/cars',
  },
  'Ocean Cruise': {
    reducer: setOceanCruiseOptions,
    to: '/shop-travel/cruises',
  },
  'River Cruise': {
    reducer: setRiverCruiseOptions,
    to: '/shop-travel/cruises?river=true',
  },
}

const PHRASES = [
  `What's your next adventure?`, 
  'Puffin watching in Iceland this summer', 
  'I want to go hiking and see waterfalls', 
  'I need a relaxing vacation in the Caribbean',
]

const EverythingSearch = ({ 
  onChange, 
  autocompleteProps, 
  textfieldProps, 
  inputProps,
  variant,
  open,
  onClose,
  ...props
}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const eToken = useSelector(({ global }) => global.eToken)
  const searchHistory = useSelector(({ global }) => global.searchHistory) || []
  const { 
    setTravelQuestionnaire,
    clearTravelQuestionnaire,
    openTravelQuestionnaire,
    clearFromState,
  } = useAction()
  const aiEverythingSearch = useSelector(({ global }) => global.aiEverythingSearch)
  const [value, setValue] = useState(null)
  const [inputValue, setInputValue] = useState('')
  // const [selectedTrip, setSelectedTrip] = useState(null)
  // const placeholder = useTypingAnimation(PHRASES)
  // const flightSearch = useFlightSearch()
  const hotelSearch = useHotelSearch()
  const experienceSearch = useExperienceSearch()
  const carSearch = useCarSearch()
  const oceanCruiseSearch = useOceanCruiseSearch({ disableSearch: true })
  const riverCruiseSearch = useRiverCruiseSearch()
  const pastTrips = useGet(GET_PAST_TRIPS, 'pastTrips', null)
  const trip = useSelector(({ global }) => global.trip)
  const { xsDown, smDown } = useBreakpoints()
  const autocompleteRef = useRef()

  const filteredSearchHistory = useMemo(() => {
    return searchHistory?.filter(o => o.search_data.type !== 'AIDestination')
  }, [searchHistory])

  // const { trip: tripData = {}, itineraries = [], counts = {}, images } = trip
  // const hasNoTripHistory = trip_data !== undefined && pastTrips !== undefined ? !trip_data?.length && !pastTrips?.length : false

  // const sortedOptions = useMemo(() => {
  //   return clone(searchHistory).filter(o => ORDER.includes(o.search_data.type)).sort((a, b) => {
  //     return ORDER.indexOf(a.search_data.type) - ORDER.indexOf(b.search_data.type);
  //   })
  // }, [searchHistory])

  // const handleOnTripSelect = useCallback(trip => {
  //   setSelectedTrip(trip)
  //   dispatch({
  //     type: GET_TRIP,
  //     id: trip?.unique_id,
  //   })
  // }, [])

  const sendToChat = useCallback(value => {

    // let temp = {}

    // if (!!selectedTrip && !!trip) {
    //   const startDate = moment(trip.trip.start_date, 'YYYY-MM-DD')
    //   const endDate = moment(trip.trip.end_date, 'YYYY-MM-DD')

    //   const durationInDays = endDate.diff(startDate, 'days')

    //   temp = {
    //     location: trip.trip.location_name,
    //     trip_duration:  `${durationInDays} days`,
    //     trip_budget: Object.fromEntries(Object.entries(trip.trip).filter(([key]) => key.includes('budget'))),
    //     itinerary_items: trip.itineraries.map(o => ({
    //       type: o.booking_data.travel_type,
    //       cost: o.total || 0,
    //       vendor: o.booking_data.segments?.map?.(o1 => o1.car_vendor)
    //     }))
    //   }
    // }

    // const currentDate = new Date().toDateString()
    dispatch({
      type: GET_GPT_EVERYTHING_SEARCH,
      data: {
        message: value,
      },
    })
  }, [trip])

  const handleSearch = useCallback(data => {
    if (CONFIG[data.type]?.reducer) {
      dispatch(CONFIG[data.type].reducer?.(data))
    }
    if (CONFIG[data.type]?.to) {
      navigate(CONFIG[data.type].to)
    } else {
      if (data.type === 'Search') sendToChat(data.value)
      if (data.type === 'Stay') hotelSearch.searchHotels({ skipSave: true })
      if (data.type === 'Experience') experienceSearch.searchExperiences({ value: data.value, skipSave: true })
      if (data.type === 'Car') return carSearch.searchCars(true)
      if (data.type === 'Ocean Cruise') return oceanCruiseSearch.searchOceanCruises(true)
      if (data.type === 'River Cruise') return riverCruiseSearch.searchRiverCruises(true)
      // if (data.type === 'Flight') flightSearch.searchFlights(true)
    }
  }, [])
  
  const onAutoSearch = newValue => {
    if (newValue?.search_data) {
      handleSearch(newValue.search_data)
    } else if (newValue?.length) {
      dispatch({
        type: ADD_TO_SEARCH_HISTORY,
        data: { type: 'Search', value: newValue },
        hash: md5(`Search${newValue}`),
      })
      sendToChat(newValue)
    }
  }

  useEffect(() => {
    if (!eToken) dispatch({ type: ENCRYPT_TOKEN })
    dispatch({
      type: GET_SEARCH_HISTORY,
    })
  }, [])

  useEffect(() => {
    if (!isEmpty(aiEverythingSearch)) {
      clearFromState(['aiDestinations'])
      clearTravelQuestionnaire()
      setTravelQuestionnaire(aiEverythingSearch)
      if (aiEverythingSearch.total_travelers) {
        dispatch(setHotelOptions({
          rooms: distributeTravelers(+aiEverythingSearch.total_travelers, +aiEverythingSearch.total_children),
        }))
      }
      const categories = [
        aiEverythingSearch.search_stays, 
        aiEverythingSearch.search_flights, 
        !!aiEverythingSearch.attractions, 
        // aiEverythingSearch.search_experiences, 
        aiEverythingSearch.search_rental_cars, 
        aiEverythingSearch.search_river_cruises, 
        aiEverythingSearch.search_ocean_cruises, 
        // aiEverythingSearch.search_general,
      ].filter(o => !!o)
      // if (aiEverythingSearch.search_flights) {
      //   navigate('/shop-travel/flights')
      // } else 
      // const destination = aiEverythingSearch.city_name || aiEverythingSearch.subdivision_name || aiEverythingSearch.country_name || null
      // if (categories.length !== 1 && (checkString(aiEverythingSearch.city_name) || checkString(aiEverythingSearch.subdivision_name) || checkString(aiEverythingSearch.country_name))) {
      if (checkString(aiEverythingSearch.city_name) || checkString(aiEverythingSearch.subdivision_name) || checkString(aiEverythingSearch.country_name)) {
        navigate(`/destination?city_name=${aiEverythingSearch.city_name || country_capitals[aiEverythingSearch.country_name] || ''}&subdivision_name=${aiEverythingSearch.subdivision_name || ''}&country_name=${aiEverythingSearch.country_name || ''}`)
      } else {
        openTravelQuestionnaire('RESULTS')
      }
      // else if (aiEverythingSearch.attractions) {
      //   experienceSearch.searchExperiences({ 
      //     value: destination ? {
      //       description: destination,
      //       query: aiEverythingSearch.attractions,
      //     } : aiEverythingSearch.attractions,
      //     // value: aiEverythingSearch.attractions,
      //     skipSave: true,
      //   })
      // } else if (aiEverythingSearch.search_stays && categories.length === 1) {
      //   dispatch(setHotelOptions({
      //     destination: destination || '',
      //     inputValue: destination || '',
      //     // amenities: [],
      //     // priceRange: [0, 1000],
      //     // userRatingRange: [0, 5],
      //     // starRatingRange: [0, 5],
      //   }))
      //   hotelSearch.onDateRangeChange([aiEverythingSearch.departure_date, aiEverythingSearch.return_date])
      //   navigate('/shop-travel/stays/hotels')
      // }
      clearFromState(['aiEverythingSearch'])
    }
  }, [aiEverythingSearch])

  const renderAutocomplete = () =>
    <Stack
      display="grid"
      gridTemplateColumns="1fr"
      gap={1}
      width="100%"
      {...props}
    >
      <Autocomplete
        ref={autocompleteRef}
        id="everything-search"
        openOnFocus
        blurOnSelect
        // open
        open={open && smDown && variant === 'nav' || undefined}
        className="rounded-full"
        options={filteredSearchHistory}
        getOptionLabel={option => getName(option.search_data)}
        filterOptions={x => x}
        renderOption={(props, option) => (
          <div
            {...props}
          >
            {getSearchHistoryElement(option.search_data)}
          </div>
        )}
        noOptionsText={null}
        autoComplete
        fullWidth
        freeSolo
        componentsProps={{
          popper: {
            className: 'rounded-full',
            placement: 'bottom-end',
            sx: {
              boxSizing: 'border-box',
              minWidth: 300,
              borderBottomLeftRadius: '25px !important',
              borderBottomRightRadius: '25px !important',
              overflow: 'hidden',
              boxShadow: 'none',
            }
            // sx: variant === 'nav' ? { width: 300 } : undefined,
            // anchorEl: () => {
            //   return document.getElementById('nav-search')
            // }
          },
        }}
        value={value}
        onChange={(event, newValue) => {
          setValue(newValue)
          if (!!onChange) onChange(newValue)
          onAutoSearch(newValue)
          if (open) onClose?.(event)
        }}
        onInputChange={(e, newInputValue) => {
          if (e.key === 'Enter') { 
            // e.stopPropagation()
            // e.preventDefault()
            if (!!onChange) onChange(newInputValue)
            onAutoSearch(newInputValue)
          }
          setInputValue(newInputValue)
        }}
        renderInput={params => {
          // const startAdornment = [
          //   ...pastTrips?.map?.((option, index) => selectedTrip?.includes(option.unique_id) && (
          //     <TripChip
          //       key={option.unique_id}
          //       {...option}
          //     />
          //   )),
          //   ...params.InputProps.startAdornment,
          // ].filter(o => !!o)

          // const startAdornment = pastTrips?.map?.((option, index) => selectedTrip?.unique_id === option.unique_id && (
          //   <TripChip
          //     key={option.unique_id}
          //     {...option}
          //     onDismiss={() => setSelectedTrip(null)}
          //   />
          // ))?.filter(o => !!o) || []

          return (
            <AnimatedPlaceholderTextField
              disableAnimation={variant === 'nav'}
              showX={smDown && variant === 'nav' && open}
              // autoFocus={smDown && variant === 'nav'}
              // autoFocus={smDown && variant === 'nav' && open}
              {...params}
              {...textfieldProps}
              placeholder={variant === 'nav' ? 'Search' : xsDown ? 'Search destinations & more' : 'Search destinations, recommendations, & more'}
              // placeholderPhrases={PHRASES}
              InputProps={{
                ...params.InputProps,
                // startAdornment,
                [variant === 'nav' ? 'startAdornment' : 'endAdornment']: variant === 'nav' && smDown ? (
                  <Box
                    tabindex="0"
                    role="button"
                    width="16px"
                    height="16px"
                    mr={1}
                    sx={{
                      cursor: 'pointer',
                      color: 'text.neutralCool_700',
                    }}
                    onClick={onClose}
                  >
                    <IconBackArrow
                      width="16"
                      height="16"
                    />
                  </Box>
                ) : (
                  <Box
                    tabindex="0"
                    role="button"
                    width="24px"
                    height="24px"
                    mr={1.5}
                    sx={{
                      cursor: 'pointer',
                      color: 'text.neutralCool_700',
                    }}
                    onClick={() => {
                      if (inputValue?.length) {
                        if (!!onChange) onChange(inputValue)
                        onAutoSearch(inputValue)
                      }
                    }}
                  >
                    <IconSearch
                      width="24"
                      height="24"
                      viewBox="0 0 20 20"
                    />
                  </Box>
                ),
                ...inputProps,
              }}
            />
          )
        }}
        PaperComponent={({ children }) => {
          return (
            <Paper
              sx={{
                ...(smDown && variant === 'nav' ? {
                  borderRadius: '0 !important',
                  borderTop: '1px solid #ECF0F4',
                  '.MuiAutocomplete-listbox': {
                    maxHeight: 'calc(100vh - 47px)',
                  },
                } : {
                  borderBottomLeftRadius: '25px',
                  borderBottomRightRadius: '25px',
                  borderTopLeftRadius: 0,
                  borderTopRightRadius: 0,
                  minWidth: 300,
                  borderStyle: 'solid',
                  borderWidth: 1,
                  borderColor: '#B3BCC6',
                }),
              }}
            >
              {children}
              {/* {inputValue?.length === 0 && !!pastTrips?.length && (
                <PastTrips
                  selectedTrip={selectedTrip}
                  onSelect={handleOnTripSelect}
                />
              )} */}
            </Paper>
          )
        }}
        // {...autocompleteProps}
        sx={{
          display: 'grid',
          // width: variant === 'nav' ? 300 : undefined,
        }}
      />
    </Stack>

  return smDown && variant === 'nav' ? (
    <Dialog
      fullScreen
      // disableAutoFocus
      // keepMounted
      onTransitionEnter={() => {
        autocompleteRef.current.querySelector('input').focus()
      }}
      open={open}
    >
      {renderAutocomplete()}
    </Dialog>
  ) : renderAutocomplete()
}

EverythingSearch.propTypes = {
  autocompleteProps: PropTypes.object,
  textfieldProps: PropTypes.object,
  inputProps: PropTypes.object,
  // startingAirport: PropTypes.string,
  onChange: PropTypes.func,
  open: PropTypes.bool,
  onClose: PropTypes.bool,
}

EverythingSearch.defaultProps = {
  variant: 'default',
  autocompleteProps: {},
  inputProps: {},
  onClose: () => {},
}

export default EverythingSearch
